// The hand-off to Global's payment page, shared by both buy flows (Quote.jsx, SchoolPortal.jsx). Global collects the
// premium (David 2026-11-04); this page never binds and never takes money. It:
//   checkout(token, selection)  after the acknowledgements: POST /api/ctr/quotes/:token/checkout, which re-quotes at the
//                               student's selection if it changed, and answers the outcome plus Global's summary link.
//   return visits               ?quote=<token> in the address (written after checkout) reloads the quote's state from
//                               GET /api/ctr/quotes/:token/state; the server derives the state, this hook only renders it.
//   watching                    after "Continue to payment", re-reads the state every 20 seconds until it leaves the
//                               hand-off (cleared, review, declined...).
// The summary link opens Global's application with no login: it is never logged, stored in the browser, or put in the
// address; only the quote token is, and the link to Global carries rel="noreferrer" so the token never reaches Global.
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api.js';

const TOKEN = /^[0-9a-f]{64}$/;
const WATCH_OUTCOMES = ['interstitial', 'binding', 'status'];
const WATCH_MS = 20000;
const WATCH_FOR_MS = 30 * 60 * 1000; // an open tab stops asking after half an hour; a reload starts it again

function tokenFromAddress() {
  try {
    const t = new URLSearchParams(window.location.search).get('quote');
    return t && TOKEN.test(t) ? t : null;
  } catch { return null; }
}

function writeTokenToAddress(token) {
  try {
    const u = new URL(window.location.href);
    if (token) u.searchParams.set('quote', token); else u.searchParams.delete('quote');
    window.history.replaceState(null, '', u.toString());
  } catch { /* the page works without it; only a reload loses the quote */ }
}

/** The checkout selection from Coverage's onCheckout payload: EVERY line as shown (the floor rung where nothing was
 *  picked), so the server compares the whole config with the quote Global holds and re-quotes only on a real difference. */
export function selectionFrom(payload, quote) {
  const p = payload || {};
  const sel = p.chosen || p.sel || {};
  const out = {};
  if (sel.liability) out.liability = { limit: sel.liability.limit, pax_sublimit: sel.liability.paxSubLimit, excluding_passengers: !!sel.liability.excludingPassengers };
  if (sel.damage) out.damage_limit = sel.damage.limit;
  if (sel.deductibleReimb) out.deductible_reimb = sel.deductibleReimb.limit;
  if (sel.medical) out.medical = sel.medical.limit;
  if (quote?.cfi_extension) out.cfi = p.cfiOn !== false;
  const types = {};
  for (const k of p.typesConfirmed || []) types[k] = true;
  if (Object.keys(types).length) out.types = types;
  return out;
}

export function useQuoteHandoff(setOutcome) {
  const [info, setInfo] = useState({ summaryUrl: null, label: null, email: null, school: null, effectiveDate: null, error: null });
  const [watching, setWatching] = useState(false);
  const tokenRef = useRef(tokenFromAddress());

  const apply = useCallback((r) => {
    setInfo((i) => ({
      summaryUrl: r.summary_url || null,
      label: r.label || null,
      email: r.email || i.email,
      school: r.school || i.school,
      effectiveDate: r.effective_date || null,
      error: null,
    }));
    setOutcome(r.outcome || 'status');
    return r;
  }, [setOutcome]);

  const refresh = useCallback(async () => {
    const t = tokenRef.current;
    if (!t) return null;
    return apply(await api.get(`/api/ctr/quotes/${t}/state`));
  }, [apply]);

  // A return visit: the address names a quote.
  useEffect(() => {
    if (!tokenRef.current) return;
    refresh()
      .then((r) => { if (r && WATCH_OUTCOMES.includes(r.outcome)) setWatching(true); })
      .catch((e) => { if (e?.status === 404) { tokenRef.current = null; writeTokenToAddress(null); } });
  }, [refresh]);

  useEffect(() => {
    if (!watching) return undefined;
    const started = Date.now();
    const id = setInterval(() => {
      if (Date.now() - started > WATCH_FOR_MS) { setWatching(false); return; }
      refresh().then((r) => { if (r && !WATCH_OUTCOMES.includes(r.outcome)) setWatching(false); }).catch(() => {});
    }, WATCH_MS);
    return () => clearInterval(id);
  }, [watching, refresh]);

  const checkout = useCallback(async (token, selection) => {
    if (!token || !TOKEN.test(token)) { setInfo((i) => ({ ...i, summaryUrl: null, error: null })); setOutcome('interstitial'); return; }
    tokenRef.current = token;
    writeTokenToAddress(token);
    setOutcome('pending');
    try {
      apply(await api.post(`/api/ctr/quotes/${token}/checkout`, { selection }));
    } catch (e) {
      // Not ready (no link stored for this quote): the hand-off shows without a button and says Global emails the link.
      // Anything else (a failed re-quote, a refused selection) must not send the student to pay for a quote they did not
      // pick: the hand-off says so and sends them back to their coverage.
      const notReady = e?.code === 'checkout_unavailable';
      const known = ['acks_required', 'too_many_changes', 'below_school_floor'];
      setInfo((i) => ({ ...i, summaryUrl: null, error: notReady ? null : (known.includes(e?.code) ? e.code : 'requote_failed') }));
      setOutcome('interstitial');
    }
  }, [apply, setOutcome]);

  const forget = useCallback(() => { tokenRef.current = null; writeTokenToAddress(null); setWatching(false); }, []);

  return { info, checkout, startWatching: () => setWatching(true), stopWatching: () => setWatching(false), watching, forget };
}
